<template>
  <div>
    <el-form size="mini" ref="form" label-position="left" label-width="170px" v-loading="loading" element-loading-spinner="atom-audit-loader">
      <el-row :gutter="30">
        <el-col :span="12">
          <h4 class="headerWithBorder">Chart Demographic</h4>

          <el-form-item size="mini" label="Patient Number">
            <el-input disabled size="mini" :maxlength="15" type="text" id="patientNumber" v-model="worksheet.patAcctNum"> </el-input>
          </el-form-item>

          <el-form-item size="mini" label="MR Number">
            <el-input :disabled="disabled" size="mini" :maxlength="15" type="text" id="mrNumber" v-model="worksheet.mrNumber"> </el-input>
          </el-form-item>

          <el-form-item size="mini" label="Payor">
            <el-input :disabled="disabled" v-model="worksheet.payorName" placeholder="Payor" itemkey="key" label="value"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Payor Detail">
            <el-input :disabled="disabled" v-model="worksheet.payorDetail" placeholder="Payor Detail" itemkey="key" label="value"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Date of Service">
            <el-date-picker :disabled="disabled" size="mini" type="date" id="dischargeDate" v-model="worksheet.dateOfService" format="MM/dd/yyyy"> </el-date-picker>
          </el-form-item>

          <el-form-item size="mini" label="Age">
            <el-input disabled v-model="worksheet.patientAge"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Patient Sex">
            <el-input disabled v-model="worksheet.patientSex"></el-input>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <h4 class="headerWithBorder">Review Information</h4>

          <el-form-item size="mini" label="Facility">
            <el-input disabled v-model="worksheet.clientName"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Auditor">
            <el-input disabled v-model="worksheet.reviewerName"></el-input>
          </el-form-item>

          <el-form-item v-if="worksheet.projectReviewTypeId === 2" size="mini" label="Original Auditor">
            <el-input disabled v-model="worksheet.originalAuditor"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Review Date">
            <el-date-picker disabled size="mini" type="date" id="revDate" v-model="worksheet.reviewDate" format="MM/dd/yyyy"> </el-date-picker>
          </el-form-item>

          <el-form-item size="mini" label="Coder">
            <el-input disabled v-model="worksheet.coder"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Provider">
            <el-input disabled v-model="worksheet.physician"></el-input>
          </el-form-item>

          <el-form-item size="mini" label="Audit Result" v-if="worksheet.auditResult">
            <div class="auditResultRow">
              <el-tag :type="auditResultTagType" size="mini" effect="light" :title="auditResultTitle">
                {{ worksheet.auditResult }}
              </el-tag>
              <el-button class="auditResultAction" type="text" size="mini" icon="mdi mdi-table-eye" @click="showSummary" :title="auditResultTitle">
                View stats
              </el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :xl="8" :sm="12" class="top-margin">
          <h4 class="headerWithBorder">Original Coding Details</h4>

          <el-form-item size="mini" label="Patient Status">
            <el-input disabled v-model="worksheet.originalPatientStatus"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <el-dialog title="Record Summary" :visible.sync="summaryDialogVisible" destroy-on-close width="75%">
      <div style="min-height: 475px;" v-loading="summaryLoading" element-loading-spinner="atom-audit-loader">
        <template v-if="!summaryLoading">
          <div v-for="row in summaryData" :key="row.patientType" style="margin-bottom: 50px;">
            <OPSummary :ref="`opSummary_${row.codeDesc}`" :item="row" v-if="worksheet.outpatientGrouperId === 1" :title="`${row.codeDesc}`"></OPSummary>
            <ASCSummary :ref="`ascSummary_${row.codeDesc}`" :item="row" v-else :title="`${row.codeDesc}`"></ASCSummary>
          </div>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import dateHelper from '@/mixins/date-helper'
  import { format, parseISO } from 'date-fns'
  import { mapMutations, mapState } from 'vuex'
  import OPSummary from '@/views/reports/dashboards/OP_Summary'
  import ASCSummary from '@/views/reports/dashboards/ASC_Summary'
  import axios from '@/utilities/axios-global'

  export default {
    name: 'coderInpatientDemographics',
    mixins: [dateHelper],
    props: ['worksheet'],
    data() {
      return {
        format: format,
        parseISO: parseISO,
        disabled: true,
        loading: false,
        summaryLoading: true,
        summaryDialogVisible: false,
        summaryData: {}
      }
    },
    components: {
      OPSummary,
      ASCSummary
    },
    methods: {
      ...mapMutations('coder/', ['SET_RESPONSE_PATSTAT', 'SET_RESPONSE_DRG', 'SET_RESPONSE_POO']),
      async showSummary() {
        if (!this.worksheet?.ascTableId) return

        this.summaryDialogVisible = true
        const payload = {
          ascTableIds: this.worksheet.ascTableId,
          includeAccts: false,
          statType: 'test'
        }

        try {
          this.summaryLoading = true
          const response = await axios.post(this.baseApiUrl + '/reports/dashboard/opBasicStat', payload)
          this.summaryData = response.data
        } catch (err) {
          this.summaryData = []
        } finally {
          this.summaryLoading = false
        }
      }
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      auditResultTagType() {
        switch (this.worksheet?.auditResult) {
          case 'No Change':
            return 'success'
          case 'FYI':
            return 'warning'
          case 'APC Financial Change':
          case 'ASC Financial Change':
            return 'danger'
          default:
            return 'info'
        }
      },
      auditResultTitle() {
        switch (this.worksheet?.auditResult) {
          case 'No Change':
            return 'There are no recommended changes on the record'
          case 'FYI':
            return `The record contains recommendations that do not affect the ${this.worksheet?.outpatientGrouperId === 1 ? 'Outpatient' : 'Ambulatory'} grouper`
          case 'APC Financial Change':
            return 'The record contains recommendations that change the APC reimbursement'
          case 'ASC Financial Change':
            return 'The record contains recommendations that change the ASC reimbursement'
          default:
            return ''
        }
      }
    }
  }
</script>

<style scoped>
  .auditResultRow {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 22px;
  }

  .auditResultAction {
    padding: 0;
    line-height: 1;
  }
</style>
